import React, { useContext, useEffect, useState } from 'react';
import { useSelector, RootStateOrAny } from 'react-redux';
import ReactSVG from 'react-svg';
import { Star, StarBorder } from '@material-ui/icons';
import { useTryToCall } from '~/hooks';
import { CallIcon, VideoIcon } from '~/assets/svgIcons';
import { checkVideoAppVersion, isUndefined } from '~/helpers';
import * as NuacomComponent from '~/components';
import AgentsList, { AgentActionsProps } from '~/components/AgentsList';
import { useStartVideoCall } from '~/hooks/useStartVideoCall';
import { AgentStatusContext } from '~/providers/AgentStatusProvider';
import {
  Container,
  HeaderControls,
  Content,
  IconButtonComponent,
  ActionStar,
  ActionGroup,
} from './styles';

/**
 * The max number of concurrent active calls allowed in the softphone
 */
const ACTIVE_CALLS_COUNT_MAX = 2;

const AgentActions = ({
  agent,
  favorite,
  loading,
  addFavoriteAgent,
}: AgentActionsProps) => {
  // HOOK's
  const tryToCall = useTryToCall();
  const {
    hasVideoPermission,
    isVideoWebSocketEnabled,
    startVideoCall,
  } = useStartVideoCall();

  const activeCallsCount = useSelector(
    (state: RootStateOrAny) => state.callCurrent?.data?.length
  );
  const systemVersion = useSelector(
    (state: RootStateOrAny) => state.system?.version
  );

  const isVideoSupported = checkVideoAppVersion(systemVersion);

  function onClick() {
    if (activeCallsCount < ACTIVE_CALLS_COUNT_MAX)
      tryToCall(agent.number, { name: agent.name || null });
  }

  const disableVideo =
    activeCallsCount >= 1 || agent.status !== '0' || !isVideoWebSocketEnabled;

  return (
    <ActionGroup>
      <ActionStar
        role="presentation"
        onClick={() => addFavoriteAgent(agent.number)}
        active={favorite}
        loading={loading}>
        {favorite ? <Star className="icon" /> : <StarBorder className="icon" />}
      </ActionStar>
      {hasVideoPermission && isVideoSupported ? (
        <IconButtonComponent
          title="Start a video call"
          aria-label="Start a video call"
          onClick={() => (disableVideo ? {} : startVideoCall(agent.number))}
          disable={disableVideo}>
          <ReactSVG src={VideoIcon} className="video-icon" />
        </IconButtonComponent>
      ) : null}
      <IconButtonComponent
        title={`Try to call agent ${agent.number}`}
        aria-label={`Try to call agent ${agent.number}`}
        onClick={() =>
          activeCallsCount >= ACTIVE_CALLS_COUNT_MAX ? {} : onClick()
        }
        disable={activeCallsCount >= ACTIVE_CALLS_COUNT_MAX}>
        <ReactSVG src={CallIcon} className="call-icon" />
      </IconButtonComponent>
    </ActionGroup>
  );
};

export default function Teams(props: any) {
  // HOOK's
  // const dispatch = useDispatch();

  // CONTEXT'S
  const { refreshAgents } = useContext(AgentStatusContext);

  // STATE's
  const [search, setSearch] = useState('');
  const [openSearch, setOpenSearch] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      if (!openSearch) setSearch('');
    }, 500);
  }, [openSearch]);

  useEffect(() => {
    // dispatch(actions.refreshWebSocket());
    refreshAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSearchChange(value: string) {
    if (!isUndefined(value)) setSearch(value);
  }

  return (
    <Container>
      <NuacomComponent.Header {...props}>
        <HeaderControls>
          {!openSearch ? (
            <>
              <NuacomComponent.BtnSearch
                data-test-id="btn-search-contacts"
                callback={setOpenSearch}
                value={openSearch}
              />
            </>
          ) : (
            <NuacomComponent.ContactsSearch
              value={search}
              onChange={onSearchChange}
              callback={setOpenSearch}
              searchApi={false}
            />
          )}
        </HeaderControls>
      </NuacomComponent.Header>

      <Content>
        <AgentsList maxHeight="auto" searchFor={search}>
          {({
            agent,
            addFavoriteAgent,
            favorite,
            loading,
          }: AgentActionsProps) => (
            <AgentActions
              agent={agent}
              favorite={favorite}
              loading={loading}
              addFavoriteAgent={addFavoriteAgent}
            />
          )}
        </AgentsList>
      </Content>
    </Container>
  );
}
